/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import {
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { spacing } from '../../shared/theme/spacing';
import { ModalShell } from '../../shared/modals/ModalShell'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { useNavigation } from '@react-navigation/native';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { addStockModalStyles as styles } from './addStockModal.styles';
import type { AddStockModalProps } from './addStockModal.types';
import { useAddStockModal } from './useAddStockModal';
import { AddStockModalProductSection } from './AddStockModalProductSection';
import { AddStockModalEntryFields } from './AddStockModalEntryFields';
import { AddStockModalSupplierPicker } from './AddStockModalSupplierPicker';

export const AddStockModal = (props: AddStockModalProps) => {
  const {
    visible,
    productQuery,
    productQueryDebounced,
    entryScanFeedback,
    entrySuggestion,
    productResults,
    productLoadingMore,
    productHasMore,
    entryQuantity,
    entryUnitCost,
    entryReason,
    entryMinQuantity,
    entryExpiry,
    entrySupplierId,
    onEntrySupplierIdChange,
    entryLotNumber,
    onEntryLotNumberChange,
    entryVariantId,
    onEntryVariantIdChange,
    availableVariants,
    savingEntry,
    lastUnitCost,
    onClose,
    onProductQueryChange,
    onOpenScanner,
    showBarcodeCameraButton = true,
    onProductQuerySubmitEditing,
    autoFocusProductQueryOnWeb = false,
    onOpenCatalog,
    onSelectProduct,
    onClearSelectedProduct,
    onEntryQuantityChange,
    onEntryUnitCostChange,
    onEntryReasonChange,
    onEntryMinQuantityChange,
    onEntryExpiryChange,
    onLoadMoreProducts,
    onSave,
    selectedProduct,
    stocks,
    suppliers,
  } = props;
  const navigation = useNavigation<{ navigate: (name: string, params?: { openDirect?: boolean; focusToken?: string }) => void }>();
  const purchasesEnabled = useHasBusinessCapability('purchases');

  const modal = useAddStockModal({
    visible,
    entryReason,
    entrySupplierId,
    productQueryDebounced,
    productResults,
    productLoadingMore,
    productHasMore,
    stocks,
    selectedProduct,
    suppliers,
    onSelectProduct,
    onLoadMoreProducts,
  });

  // ── Cambios sin guardar: baseline = snapshot de los campos al abrir ──
  // (el open puede venir con producto preseleccionado desde el detalle de
  // stock — eso no cuenta como dirty). Mismo patrón que CatalogProductFormModal.
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const [baselineKey, setBaselineKey] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const formSnapshot = () =>
    JSON.stringify({
      productQuery,
      productId: selectedProduct?.id ?? null,
      entryQuantity,
      entryUnitCost,
      entryReason,
      entryMinQuantity,
      entryExpiry,
      entrySupplierId,
      entryLotNumber,
      entryVariantId,
    });
  // Baseline al abrir (ajuste de estado en render — evita setState en efecto):
  // solo cuando se abre — los cambios posteriores son el dirty check.
  const [wasVisible, setWasVisible] = useState(false);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (visible) {
      setBaselineKey(formSnapshot());
      setDiscardConfirmOpen(false);
    }
  }

  const isDirty = formSnapshot() !== baselineKey;
  const closeLocked = savingEntry;
  const guardedClose = useCallback(() => {
    if (closeLocked) return;
    // Si el confirm ya está arriba, Escape/back lo cierra a él.
    if (discardConfirmOpen) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: 'Descartar ingreso',
      message: 'Hay datos cargados sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  }, [closeLocked, discardConfirmOpen, isDirty, onClose, confirmDiscard]);

  // Motivo "Compra" → la recepción de compra vive en el módulo Compras.
  // Form limpio: navegar directo (no hay nada que perder). Con datos cargados:
  // confirm explícito — el salto descarta lo ingresado.
  const goToPurchases = useCallback(() => {
    onClose();
    navigation.navigate('Purchases', { openDirect: true, focusToken: String(Date.now()) });
  }, [onClose, navigation]);
  const { confirm: confirmGoPurchases, ConfirmModal: GoPurchasesConfirmModal } = useOperationalConfirm();
  const [purchaseConfirmOpen, setPurchaseConfirmOpen] = useState(false);
  const handleEntryReasonChange = (value: AddStockModalProps['entryReason']) => {
    if (value === 'purchase_reception' && purchasesEnabled) {
      if (!isDirty) {
        goToPurchases();
        return;
      }
      if (purchaseConfirmOpen) return;
      setPurchaseConfirmOpen(true);
      void confirmGoPurchases({
        title: 'Ir a Compras',
        message: 'Lo que cargaste acá se pierde. La recepción de compra se hace en Compras, con proveedor e ítems.',
        confirmLabel: 'Ir a Compras',
        iconName: 'arrow-forward-outline',
        presentation: 'overlay',
      }).then((confirmed) => {
        setPurchaseConfirmOpen(false);
        if (confirmed) goToPurchases();
      });
      return;
    }
    onEntryReasonChange(value);
  };

  return (
    <>
      <ModalShell
        visible={visible}
        onClose={guardedClose}
        appearance="ticket"
        kicker="Stock"
        title="Ingresar stock"
        subtitle="Cargá la mercadería que entró."
        widthVariant="wide"
        scrollRef={modal.scrollRef}
        contentRef={modal.scrollContentRef}
        // overlay (no sibling): el confirm "Ir a Compras" se renderiza como
        // capa dentro del Modal nativo — como sibling queda DETRÁS del shell.
        overlay={GoPurchasesConfirmModal}
        scrollBottomInset={spacing.sm}
        scrollPadding={spacing.md}
        scrollContentStyle={{ gap: spacing.md }}
        footer={
          <View style={[styles.modalActionsWrap, modalExperience.footerActions]}>
            <TouchableOpacity style={[posUi.secondaryButton, styles.modalActionButton, savingEntry && posUi.disabledCard]} onPress={guardedClose} disabled={savingEntry} accessibilityRole="button" accessibilityLabel="Cancelar ingreso de stock">
              <Text style={posUi.secondaryButtonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[posUi.primaryButton, styles.modalActionButton, savingEntry && posUi.disabledCard]}
              onPress={onSave}
              disabled={savingEntry}
              accessibilityRole="button"
              accessibilityLabel={savingEntry ? 'Guardando ingreso de stock' : 'Guardar ingreso de stock'}
            >
              <Text style={posUi.primaryButtonText}>{savingEntry ? 'Guardando…' : 'Guardar ingreso'}</Text>
            </TouchableOpacity>
          </View>
        }
      >
        <AddStockModalProductSection
          productQuery={productQuery}
          selectedProduct={selectedProduct}
          entryScanFeedback={entryScanFeedback}
          entrySuggestion={entrySuggestion}
          onProductQueryChange={onProductQueryChange}
          onOpenScanner={onOpenScanner}
          showBarcodeCameraButton={showBarcodeCameraButton}
          onProductQuerySubmitEditing={onProductQuerySubmitEditing}
          autoFocusProductQueryOnWeb={autoFocusProductQueryOnWeb}
          onOpenCatalog={onOpenCatalog}
          onClearSelectedProduct={onClearSelectedProduct}
          suggestionsOpen={modal.suggestionsOpen}
          suggestionItems={modal.suggestionItems}
          productLoadingMore={productLoadingMore}
          productHasMore={productHasMore}
          onLoadMoreProducts={onLoadMoreProducts}
          onPickSuggestion={modal.handlePickSuggestion}
          stockQuantity={selectedProduct ? stocks.find((s) => s.productId === selectedProduct.id)?.quantity ?? null : null}
        />
        <AddStockModalEntryFields
          entryQuantity={entryQuantity}
          entryUnitCost={entryUnitCost}
          entryReason={entryReason}
          entryMinQuantity={entryMinQuantity}
          entryExpiry={entryExpiry}
          entrySupplierId={entrySupplierId}
          entryLotNumber={entryLotNumber}
          lastUnitCost={lastUnitCost}
          selectedProduct={selectedProduct}
          onEntryQuantityChange={onEntryQuantityChange}
          onEntryUnitCostChange={onEntryUnitCostChange}
          onEntryReasonChange={handleEntryReasonChange}
          onEntryMinQuantityChange={onEntryMinQuantityChange}
          onEntryExpiryChange={onEntryExpiryChange}
          onEntryLotNumberChange={onEntryLotNumberChange}
          entryVariantId={entryVariantId}
          onEntryVariantIdChange={onEntryVariantIdChange}
          availableVariants={availableVariants}
          supplierLabel={modal.supplierLabel}
          onOpenSupplierPicker={() => modal.setSupplierPickerOpen(true)}
          purchasesEnabled={purchasesEnabled}
        />
      </ModalShell>

      <AddStockModalSupplierPicker
        visible={modal.supplierPickerOpen}
        entrySupplierId={entrySupplierId}
        suppliers={modal.suppliers}
        onClose={() => modal.setSupplierPickerOpen(false)}
        onSelectSupplier={(supplierId) => {
          onEntrySupplierIdChange(supplierId);
          modal.setSupplierPickerOpen(false);
        }}
      />
      {DiscardConfirmModal}
    </>
  );
};
